// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import * as React from 'react';

import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { useTranslation } from 'react-i18next';

import { getDispositionCode } from './../../lib/DispositionCodes';

import { logPageAction } from './../../lib/ApplicationInsights';
import { orderEventStateLookUp } from './../../helpers/orderEventLookup';

import { ClaimOrderLineItemAttributes } from './../../store/orderCreation';

import Styles from './pizzaTracker.module.scss';


import { IAppointmentDetails, IPartnerDetails } from './../../businessLogic/sagas/serviceRequest/payloadTypes';
import { OrderType } from './../../constants/orderType';
import { TravelOutcomeData } from './../../constants/travelOutcome';
import { Link } from 'office-ui-fabric-react';

interface Stage {
    carrierName?: string;
    isChecked: boolean;
    processingOutcome?: string;
    processingResultCode?: string;
    text: string;
    timestamp?: Date | null;
    trackingNumber?: string;
    trackingUrl?: string;
    partnerName?: string;
    partnerComments?: string;
    initialAppointmentSchedule?: Date | null;
    confirmedAppointmentSchedule?: Date | null;
    travelOutcome?: string;
}

interface PropsType {
    states: States;
    itemAttributes: number;
    progressName: string;
    partnerDetails: IPartnerDetails;
    appointmentDetails: IAppointmentDetails;
    orderType: string;
    travelOutcome: string;
    onPartnerCommentShowMoreClick: () => void;
}

interface State {
    isOptional?: boolean;
    isComplete?: boolean;
    details?: {
        carrierName?: string;
        carrierTrackingNumber?: string;
        carrierTrackingUrl?: string;
        eventTimestamp: string;
        processingOutcome?: string;
        processingResultCode?: string;
    };
    eventTrigger: string;
}

interface States extends Array<State> { }

function onTrackingUrlClick() {
    logPageAction('UPS tracking link clicked');
}

function RenderCircle({ taskKey, isChecked }: { taskKey: number, isChecked: boolean}) {
    const { t } = useTranslation();
    return isChecked ? (
        <div aria-label={t('task_checked', {taskKey})} className={Styles.stageCircleChecked}>
            <Icon className={Styles.stageChecked} iconName="CheckMark"/>
        </div>
    ) : (
        <div aria-label={t('task_not_checked', {taskKey})} className={Styles.stageCircle}/>
    );
}

function RenderConnector({ isLastStage }: { isLastStage: boolean }) {
    return !isLastStage ? (
        <div className={Styles.stageConnector} />
    ) : null;
}

function RenderTexts(props: { stage: Stage, onPartnerCommentShowMoreClick: () => void }) {
    const { stage, onPartnerCommentShowMoreClick } = props;
    const { t } = useTranslation();

    function showMorePartnerCommentClick(){
        onPartnerCommentShowMoreClick();
    }

    return (
        <div className={stage.isChecked ? Styles.textChecked : Styles.text}>
            <p className={Styles.message}>{stage.text}</p>
            {stage.timestamp && (
                <div className={Styles.stageTimeStamp}>{stage.timestamp.toLocaleDateString()}</div>
            )}
            {stage.partnerName && (
                <div className={Styles.onsiteDetails}>{t('onsite_partner')} : {stage.partnerName}</div>
            )}
            {stage.initialAppointmentSchedule && (
                <div className={Styles.onsiteDetails}>{t('default_onsite_appointment')} : {(new Date(stage.initialAppointmentSchedule))
                    .toLocaleDateString(undefined, {year: 'numeric', month: 'short', day: '2-digit'})}</div>
            )}
            {stage.confirmedAppointmentSchedule && (
                <div className={Styles.onsiteDetails}>{t('confirmed_onsite_appointment')} : {(new Date(stage.confirmedAppointmentSchedule))
                    .toLocaleString(undefined, {year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit'})}</div>
            )}
            {stage.partnerComments && (stage.partnerComments.length>30 ? (
                <div className={Styles.onsiteDetails}>
                    {t('partner_comments')} : {stage.partnerComments.substring(0, 30)}...... <Link onClick={showMorePartnerCommentClick}> show more</Link>
                </div>
            ):
            (
                <div className={Styles.onsiteDetails}>
                    {t('partner_comments')} : {stage.partnerComments}
                </div>
            ))}
            {stage.travelOutcome && (
                <div className={Styles.onsiteDetails}>{t('travel_outcome')} : {stage.travelOutcome}</div>
            )}
            {stage.trackingUrl && <a
                onClick={onTrackingUrlClick}
                href={stage.trackingUrl}
                target="_blank"
            >
                {stage.carrierName}:&nbsp;{stage.trackingNumber}
            </a>}
            {stage.processingOutcome && <div>
                {stage.processingOutcome.toUpperCase()}:&nbsp;
                {getDispositionCode(stage.processingOutcome)}
            </div>}
            {stage.processingResultCode && <div>
                {stage.processingResultCode.toUpperCase()}:&nbsp;
                {getDispositionCode(stage.processingResultCode)}
            </div>}
        </div>
    );
}

function RenderStage(stage: Stage, key: number, isLastStage: boolean,  onPartnerCommentShowMoreClick: () => void) {
    return (
        <li key={key} className={Styles.stage}>
            <div className={Styles.stageIcon}>
                <RenderCircle taskKey={key} isChecked={stage.isChecked} />
                <RenderConnector isLastStage={isLastStage} />
            </div>
            <RenderTexts stage={stage} onPartnerCommentShowMoreClick={onPartnerCommentShowMoreClick}/>
        </li>
    );
}

function generateEventTitle(event: string, itemAttributes: number, progressName: string): string {
    const { t } = useTranslation();

    switch (event.toLowerCase()) {
        case 'ctn_create':
            if (itemAttributes === ClaimOrderLineItemAttributes.AdvancedExchange) {
                return t('we_are_waiting_to_receive_your_return_device');
            }

        case 'rd_received':
            if (progressName.toLowerCase() === 'forwardstockexchange' || progressName.toLowerCase() === 'rrt') {
                return t('your_package_has_arrived_at_our_service_center');
            }
            if (itemAttributes === ClaimOrderLineItemAttributes.AdvancedExchange) {
                return t('we_have_received_your_return_device');
            }

        case 'od_pgi':
            if (
                itemAttributes === ClaimOrderLineItemAttributes.AdvancedExchange ||
                progressName.toLowerCase() === 'forwardstockexchange' ||
                itemAttributes === ClaimOrderLineItemAttributes.SendImmediately
            ) {
                return t('your_replacement_device_has_shipped');
            }

        case 'delivery_cd':
            if (itemAttributes === ClaimOrderLineItemAttributes.AdvancedExchange || itemAttributes === ClaimOrderLineItemAttributes.SendImmediately) {
                return t('your_replacement_device_has_been_delivered');
            }
            if (progressName.toLowerCase() === 'forwardstockexchange') {
                return t('your_replacement_device_is_ready_for_pickup');
            }

        default:
            return t(orderEventStateLookUp(event));
    }
}

function translateText(text?: string){
    const { t } = useTranslation();
    if(text) return t(text);

    return '';
}

// states: States, itemAttributes: number, progressName: string, partnerDetails: IPartnerDetails, appointmentDetails: IAppointmentDetails, orderType: string
function parseStates(props: PropsType): Stage[] {
    const keyVaules = {
        orderCreated: 'nt_released',
        orderConfirmed: 'nt_confirmed',
        partnerTravelled: 'onsite_partner_travelled',
        orderCancelled: 'nt_cancel'
    };
    const {states, itemAttributes, progressName, partnerDetails, appointmentDetails, orderType, travelOutcome} = props;
    const travelOutcomeText = translateText(TravelOutcomeData.find((item) => item.key === travelOutcome)?.text);
    const stages = states
        .filter((state: State) => (!state.isOptional || state.isComplete))
        .map((state: State) => (
            (OrderType[orderType] === OrderType.Z4E) ? {
                isChecked: state.isComplete || false,
                text: generateEventTitle(state.eventTrigger, itemAttributes, progressName),
                ...(state.details && state.eventTrigger === keyVaules.orderCreated && {
                    timestamp: state.details.eventTimestamp ? new Date(state.details.eventTimestamp) : null,
                    partnerName: partnerDetails?.name || '',
                    initialAppointmentSchedule: appointmentDetails.initialAppointmentSchedule ?
                        new Date(appointmentDetails.initialAppointmentSchedule) : null,
                }),
                ...(state.details && state.eventTrigger === keyVaules.orderConfirmed && {
                    timestamp: state.details.eventTimestamp ? new Date(state.details.eventTimestamp) : null,
                    confirmedAppointmentSchedule: appointmentDetails.confirmedAppointmentSchedule ?
                        new Date(appointmentDetails.confirmedAppointmentSchedule): null,
                    partnerComments: partnerDetails?.partnerComments || '',
                }),
                ...(state.details && state.eventTrigger === keyVaules.partnerTravelled && {
                    timestamp: state.details.eventTimestamp ? new Date(state.details.eventTimestamp) : null,
                    travelOutcome: travelOutcomeText,
                }),
                ...(state.details && state.eventTrigger === keyVaules.orderCancelled && {
                    timestamp: state.details.eventTimestamp ? new Date(state.details.eventTimestamp) : null
                }),
            } : {
                isChecked: state.isComplete || false,
                text: generateEventTitle(state.eventTrigger, itemAttributes, progressName),
                ...(state.details && {
                    carrierName: state.details.carrierName,
                    processingOutcome: state.details.processingOutcome || '',
                    processingResultCode: state.details.processingResultCode || '',
                    timestamp: state.details.eventTimestamp ? new Date(state.details.eventTimestamp) : null,
                    trackingNumber: state.details.carrierTrackingNumber,
                    trackingUrl: state.details.carrierTrackingUrl,
                }),
            }));

    stages.sort((a: any, b: any) => b.isChecked - a.isChecked);
    return stages;
}

export default function PizzaTracker(props: PropsType) {
    const stages = parseStates(props);
    return (
        <ul className={Styles.pizzaTracker}>
            {
                stages.map((stage: Stage, index: number) => {
                    return RenderStage(stage, index, stages.length - 1 === index, props.onPartnerCommentShowMoreClick);
                })
            }
        </ul>
    );
}